import { visibleActionStatusOptions } from 'components/IAP/ICSConstants';

export type PositionTaskStatusSlice = {
  status: string;
  count: number;
};

export type PositionTaskStatusChart = {
  positionId: string;
  positionName: string;
  total: number;
  slices: PositionTaskStatusSlice[];
};

type CountableAction = {
  archived?: boolean | number | null;
  assignee_kind?: string | null;
  assignee_id?: string | null;
  status?: string | null;
};

type CountablePosition = {
  id: string;
  name: string;
};

/** Product Tours / roster chrome: Analytics is HWCG-only. */
export function rosterAnalyticsVisible(hwcg: boolean): boolean {
  return hwcg;
}

function isAssignedToPosition(
  action: CountableAction,
  positionId: string
): boolean {
  return (
    !action.archived &&
    action.assignee_kind === 'position' &&
    action.assignee_id === positionId
  );
}

/**
 * Status counts for tasks assigned to a seat. Zero-count statuses are omitted
 * so the pie only draws work that exists. Status order follows the HWCG
 * picker.
 */
export function positionTaskStatusSlices(
  actions: readonly CountableAction[],
  positionId: string
): PositionTaskStatusSlice[] {
  const counts = new Map<string, number>();
  for (const action of actions) {
    if (!isAssignedToPosition(action, positionId) || !action.status) continue;
    counts.set(action.status, (counts.get(action.status) ?? 0) + 1);
  }
  return visibleActionStatusOptions(true)
    .map((option) => ({
      status: option.value,
      count: counts.get(option.value) ?? 0,
    }))
    .filter((slice) => slice.count > 0);
}

export function positionTaskStatusCharts(
  actions: readonly CountableAction[],
  positions: readonly CountablePosition[]
): PositionTaskStatusChart[] {
  return positions.map((position) => {
    const slices = positionTaskStatusSlices(actions, position.id);
    return {
      positionId: position.id,
      positionName: position.name,
      total: slices.reduce((sum, slice) => sum + slice.count, 0),
      slices,
    };
  });
}

/** Hex fills aligned with `actionStatusBadgeVariant` / timeline bars. */
export const POSITION_TASK_STATUS_COLORS: Record<string, string> = {
  Planned: '#2563eb',
  'Current/Ongoing': '#d97706',
  Blocked: '#e11d48',
  'Submitted for Review': '#7c3aed',
  Approved: '#059669',
  Closed: '#16a34a',
};

export function positionTaskStatusColor(status: string): string {
  return POSITION_TASK_STATUS_COLORS[status] ?? '#64748b';
}
